import {
  DOCUMENT,
  LocationStrategy,
} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Injector,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewEncapsulation,
} from '@angular/core';
import {
  GuardsCheckStart,
  NavigationCancel,
  NavigationEnd,
  NavigationSkipped,
  NavigationStart,
  Params,
  RouteConfigLoadEnd,
  Router,
  Scroll,
} from '@angular/router';
import {
  ErrorMessage,
} from '@dapi/sdk';
import {
  createDESLoggerLogServerClient,
  DESLogServerLoggerClientOptions,
  LOG_SERVER_LOGGER_CLIENT_OPTIONS,
} from '@des/services/logging';
import {
  CheckInPaymentRecordModel,
  selectCheckInPaymentRecordsEntities,
  selectCheckInPaymentRecordsStorePendingStatus,
} from '@des/store/check-in-payment-records';
import {
  selectJourneyAlternativeOffersIsPendingStatus,
} from '@des/store/journey-alternative-offers';
import {
  JourneyModel,
  selectJourneyElementCommons,
  selectJourneysEntities,
} from '@des/store/journeys';
import {
  MessagesStore,
  resetMessages,
  selectAllErrorMessages,
} from '@des/store/messages';
import {
  RegulatoryDetailsReplyDataModel,
  selectRegulatoryDetailsEntities,
} from '@des/store/regulatory-details';
import {
  SeatmapModel,
  selectSeatmapsEntities,
  selectSeatmapsState,
} from '@des/store/seatmaps';
import {
  Dictionary,
} from '@ngrx/entity';
import {
  select,
  Store,
} from '@ngrx/store';
import {
  LangChangeEvent,
} from '@ngx-translate/core';
import {
  EventTrackState,
  selectEventTrackState,
} from '@o3r/analytics';
import {
  ApiFactoryService,
} from '@o3r/apis-manager';
import {
  PlaceholderRulesEngineActionHandler,
} from '@o3r/components/rules-engine';
import {
  ConfigurationState,
} from '@o3r/configuration';
import {
  ConfigurationRulesEngineActionHandler,
} from '@o3r/configuration/rules-engine';
import {
  AsyncStoreItem,
  isProductionEnvironment,
  TemplateContext,
} from '@o3r/core';
import {
  DynamicContentService,
} from '@o3r/dynamic-content';
import {
  AssetRulesEngineActionHandler,
} from '@o3r/dynamic-content/rules-engine';
import {
  CUSTOM_LOCALIZATION_CONFIGURATION_TOKEN,
  LocalizationConfiguration,
  LocalizationService,
  TextDirectionService,
} from '@o3r/localization';
import {
  LocalizationRulesEngineActionHandler,
} from '@o3r/localization/rules-engine';
import {
  LoggerService,
} from '@o3r/logger';
import {
  isCapacitorContext,
} from '@o3r/mobile';
import {
  RulesEngineRunnerService,
  Ruleset,
  setRulesetsEntities,
} from '@o3r/rules-engine';
import {
  AnalyticsService,
  DataLakeState,
  Flow,
} from '@refx/analytics-core';
import {
  AppBootstrapConfig,
  BOOTSTRAP_CONFIGURATION_TOKEN,
  DEFAULT_LOCALES,
  defaultLocalizationAppProp,
  getMainLangFromLocale,
} from '@refx/shared-common';
import {
  PopInContextInput,
} from '@refx/shared-components';
import {
  ABTestingBridgeService,
  AnalyticsUtilityService,
  AppConfigService,
  AppInitializationService,
  DocumentTitleService,
  DynamicLocaleService,
  LazyLoaderDialogService,
  LoadingStateService,
  RouterLoadingStateService,
  SkeletalLoaderUtilityService,
  TagsService,
  UiStatusLoadingStateService,
} from '@refx/shared-services';
import {
  AppConfigStore,
  selectAppConfigAnimationsDisabled,
  UiStatusStore,
  UpdateUiStatus,
} from '@refx/shared-store';
import {
  CancelCheckinState,
  CancelCheckinStore,
  selectCancelCheckinState,
  selectSsciPaymentStatusIsPending,
} from '@refx/ssci-store';
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  Observable,
  Subscription,
  withLatestFrom,
} from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
} from 'rxjs/operators';
import {
  getDigitalTouchpointData,
  skeletalRoutes,
} from '../configuration/index';
import {
  environment,
} from '../environments';
import {
  AppPreferencesFactsService,
  CheckoutPaymentInformationFactsService,
  DeviceDetectionFactsService,
  JourneyInformationFactsService,
  PortalFactsService,
  SeatmapFactsService,
  TechnicalDetailsFactsService,
} from '../facts/index';

@Component({
  selector: 'app',
  templateUrl: './app.template.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  /**
   * List of subscriptions to unsubscribe on destroy
   */
  protected subscriptions: Subscription[] = [];

  private mutationObserver: MutationObserver;

  public isPending$: Observable<boolean | undefined>;

  /**
   * Observable that emits when the application has completed loading based on the TTI (Time to Interact) computation
   */
  public loadingComplete$: Observable<boolean>;

  /**
   * Boolean indicating if the animations should be disabled
   *
   * @see https://angular.io/api/animations/trigger#disabling-animations
   */
  public animationsDisabled$: Observable<boolean>;

  public showDebugPanel = false;

  /**
   * Boolean to track the activation of the first route
   */
  public firstRouteActivated = false;

  /**
   * Boolean that decides if the skeletal loader should be displayed
   */
  public displaySkeletalLoader = false;

  /**
   * A subject to trigger the monitoring of the router events pertaining to the loading states
   */
  protected monitorRouterStatus$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /**
   * Is in Capacitor context
   */
  public isCapacitorContext: Promise<boolean>;

  public displayDebugPanel = () => import(/* webpackChunkName: "dev-tools" */ '@refx/shared-components').then((module) => module.DevtoolsPanelComponentStandalone);

  constructor(
    @Inject(BOOTSTRAP_CONFIGURATION_TOKEN) private readonly bootstrapConfig: AppBootstrapConfig,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(CUSTOM_LOCALIZATION_CONFIGURATION_TOKEN) public customLocalizationConfig: LocalizationConfiguration,
    @Inject(LOG_SERVER_LOGGER_CLIENT_OPTIONS) private readonly logServerLoggerClientOptions: DESLogServerLoggerClientOptions,
    private readonly localizationService: LocalizationService,
    private readonly textDirectionService: TextDirectionService,
    private readonly store: Store<AppConfigStore & DataLakeState & ConfigurationState & UiStatusStore & CancelCheckinStore & MessagesStore>,
    public appConfigService: AppConfigService,
    private readonly router: Router,
    private readonly abTestingBridgeService: ABTestingBridgeService,
    private readonly analyticsService: AnalyticsService,
    private readonly injector: Injector,
    titleService: DocumentTitleService,
    private readonly rulesEngine: RulesEngineRunnerService,
    private readonly placeholderRulesEngineActionHandler: PlaceholderRulesEngineActionHandler,
    private readonly localizationRulesEngineActionHandler: LocalizationRulesEngineActionHandler,
    private readonly assetRulesEngineActionHandler: AssetRulesEngineActionHandler,
    private readonly configurationRulesEngineActionHandler: ConfigurationRulesEngineActionHandler,
    private readonly appInitializationService: AppInitializationService,
    private readonly checkoutPaymentInformationFacts: CheckoutPaymentInformationFactsService,
    private readonly dynamicContentService: DynamicContentService,
    private readonly deviceDetectionFacts: DeviceDetectionFactsService,
    private readonly appPreferencesFacts: AppPreferencesFactsService,
    private readonly journeyInformationFacts: JourneyInformationFactsService,
    private readonly portalFacts: PortalFactsService,
    private readonly seatmapFacts: SeatmapFactsService,
    private readonly technicalDetailsFacts: TechnicalDetailsFactsService,
    private readonly analyticsUtilityService: AnalyticsUtilityService,
    private readonly apiFactoryService: ApiFactoryService,
    private readonly loggerService: LoggerService,
    private readonly loadingStateService: LoadingStateService,
    private readonly routerLoadingService: RouterLoadingStateService,
    private readonly skeletalLoaderUtilityService: SkeletalLoaderUtilityService,
    private readonly uiStatusLoadingService: UiStatusLoadingStateService,
    private readonly lazyLoaderDialogService: LazyLoaderDialogService,
    private readonly locationStrategy: LocationStrategy,
    private readonly renderer: Renderer2
  ) {
    this.appInitializationService.initialize();

    if (!environment.DEBUG_MODE && !!this.bootstrapConfig.loggingBaseUrl) {
      this.loggerService.registerClient(createDESLoggerLogServerClient(this.apiFactoryService, this.logServerLoggerClientOptions));
    }

    this.setBodyLanguageDirection();
    this.injector.get(TagsService);
    this.injector.get(DynamicLocaleService);

    this.analyticsService.toggleShowSensitiveData(this.appInitializationService.shouldCollectSensitiveData());
    if (this.bootstrapConfig.downloadFallbackLanguageChunk) {
      this.localizationService.getTranslateService().setDefaultLang(defaultLocalizationAppProp.fallbackLanguage || DEFAULT_LOCALES.ENGB);
    }
    this.localizationService.configure();
    this.setDOMLanguage();
    this.subscriptions.push(this.textDirectionService.onLangChangeSubscription());
    this.animationsDisabled$ = store.pipe(select(selectAppConfigAnimationsDisabled));

    this.isSkeletalLoaderEnabled();

    this.isCapacitorContext = isCapacitorContext();

    this.setupAnalytics();
    this.store.dispatch(UpdateUiStatus({
      isPending: false
    }));

    // If time out, display Response timeout popup
    this.subscriptions.push(
      this.store.pipe(
        select(selectAllErrorMessages),
        filter((messages) => messages.some((errorMessage: ErrorMessage) => errorMessage.title.includes('Response timeout')))
      ).subscribe(() => {
        // Need the first value of the observable to emit in order for the dialog to open
        firstValueFrom(

          this.lazyLoaderDialogService.open(
            () => import('@refx/shared-components').then((module) => module.TimeOutDialogComponent),
            {
              data: {
                config: {},
                inputs: {},
                outputs: {}
              }
            }
          )
        );
        // clear the messages in the store after opening the timeout dialog
        this.store.dispatch(resetMessages());
      })
    );

    this.loadingComplete$ = this.store.pipe(
      select(selectEventTrackState),
      map((state: EventTrackState) => !!state && state.isTTIComputed)
    );
  }

  private setupRulesets() {
    this.rulesEngine.actionHandlers.add(this.placeholderRulesEngineActionHandler);
    this.rulesEngine.actionHandlers.add(this.localizationRulesEngineActionHandler);
    this.rulesEngine.actionHandlers.add(this.assetRulesEngineActionHandler);
    this.rulesEngine.actionHandlers.add(this.configurationRulesEngineActionHandler);
    this.appPreferencesFacts.register();
    this.checkoutPaymentInformationFacts.register();
    this.deviceDetectionFacts.register();
    this.journeyInformationFacts.register();
    this.portalFacts.register();
    this.seatmapFacts.register();
    this.technicalDetailsFacts.register();
    this.abTestingBridgeService.register();

    const rulesetsFilePaths = [];
    if (this.bootstrapConfig.useGeneratedRulesets) {
      rulesetsFilePaths.push('rules/rulesets.json');
    }
    if (this.bootstrapConfig.useAssetsRulesets) {
      rulesetsFilePaths.push('assets/rules/rulesets.json');
    }

    if (rulesetsFilePaths.length > 0) {
      const rulesetsFetch$: Observable<Promise<Ruleset[]>>[] = rulesetsFilePaths.map(
        (rulesetsFilePath) => this.dynamicContentService.getContentPathStream(rulesetsFilePath).pipe(
          map(async (path: string) => {
            const resultCall = await fetch(path);
            return resultCall.ok ? (await resultCall.json()).rulesets ?? [] : [];
          })
        )
      );

      this.subscriptions.push(
        combineLatest(rulesetsFetch$).subscribe(
          async (rulesetsPromises: Promise<Ruleset[]>[]) => {
            const fetchedRulesets = await Promise.all(rulesetsPromises);
            this.store.dispatch(setRulesetsEntities({
              entities: Array.prototype.concat(...fetchedRulesets)
            }));
          }
        )
      );
    }
  }

  /**
   * Set the language direction on the body element.
   * It is required to align the skeleton loader with the language direction correctly.
   */
  private setBodyLanguageDirection() {
    const language = this.localizationService.getCurrentLanguage();
    const direction = this.customLocalizationConfig.rtlLanguages.includes(language.split('-')[0]) ? 'rtl' : 'ltr';
    this.renderer.setAttribute(this.document.body, 'dir', direction);
  }

  private setupAnalytics() {
    this.analyticsService.configureDataLake('RefX', 'RefX', {
      digitalTouchpointDetails: {
        flow: 'SSCI',
        name: 'RefX_offtheshelf',
        clientPhase: isProductionEnvironment(this.document.body.dataset) ? 'PRD' : this.bootstrapConfig.environment,
        language: getMainLangFromLocale(this.localizationService.getCurrentLanguage()).toUpperCase()
      }
    });
    this.analyticsService.setCurrentFlow(Flow.CHECKIN);
    const digitalTouchpointData = getDigitalTouchpointData();
    this.analyticsService.setupIsProd(isProductionEnvironment(document.body.dataset));
    const portalFacts = this.appInitializationService.getPortalFacts();
    const countryCodeFromPortalFacts = (portalFacts || []).find((portalFact) => portalFact.key === 'countryCode');
    this.analyticsService.setupDigitalTouchPoint(
      getMainLangFromLocale(this.localizationService.getCurrentLanguage()),
      digitalTouchpointData.digitalTouchPoint?.touchpointInfo.market || countryCodeFromPortalFacts?.value || this.appInitializationService.getPointOfSale() || '',
      digitalTouchpointData.digitalTouchPoint?.category?.primaryCategory,
      digitalTouchpointData.digitalTouchPoint?.touchpointInfo.officeID,
      digitalTouchpointData.digitalTouchPoint?.touchpointInfo.sessionID,
      digitalTouchpointData.digitalTouchPoint?.category?.subCategory || Flow.CHECKIN,
      digitalTouchpointData.digitalTouchPoint?.touchpointInfo.site,
      digitalTouchpointData.digitalTouchPoint?.touchpointInfo.airlineCode,
      digitalTouchpointData.digitalTouchPoint?.touchpointInfo.bookingFlow,
      digitalTouchpointData.digitalTouchPoint?.touchpointInfo.currency
    );
  }

  /**
   * Set the language in the DOM on language change
   * until https://github.com/ngx-translate/core/issues/565 is implemented
   */
  private setDOMLanguage() {
    this.subscriptions.push(
      this.localizationService.getTranslateService().onLangChange.subscribe((params: LangChangeEvent) => {
        if (this.document.documentElement) {
          this.document.documentElement.lang = params.lang;
        }
      })
    );
  }

  private isEntitiesPending(entities: Dictionary<JourneyModel | SeatmapModel | RegulatoryDetailsReplyDataModel | CheckInPaymentRecordModel>): boolean {
    return Object.values(entities).some((entity) => !!entity && !!entity.isPending);
  }

  /**
   * Adds the click event on an element that should pop-in
   *
   * @param element the element to attach the click event
   */
  private addPopInClickEventOnElement(element: Element) {
    // Once a pop-in trigger has been detected, add a click event listener on it to intercept the navigation and open the pop in
    element.addEventListener('click', (event) => {
      event.preventDefault();
      this.openPopIn(element);
    });
    // Remove the trigger-pop-in class from the element to avoid attaching multiple click events on the same link
    element.classList.remove('trigger-pop-in');
  }

  /**
   * Checks if an element contains the trigger-pop-in CSS class.
   * If not, check the child nodes for the same.
   *
   * @param element to check the existence of trigger-pop-in CSS class
   */
  private checkTriggerPopInElement(element: Element) {
    if (element.classList.contains('trigger-pop-in')) {
      this.addPopInClickEventOnElement(element);
    } else if (element.childNodes) {
      element.childNodes.forEach((childNode: ChildNode) => {
        if (childNode instanceof Element) {
          this.checkTriggerPopInElement(childNode);
        }
      });
    }
  }

  /**
   * Initializes the mutation observer to be used on body element.
   * The mutation observer listens to DOM element additions or removal. We are using it to detect DOM elements containing "trigger-pop-in" CSS class.
   * If we detect one, we add a click listener on it to prevent the default behaviour (navigation for <a> elements) and we open the pop in.
   */
  private initMutationObserver() {
    // Listening for changes on body
    const targetNode = document.body;
    // Listen only to childList mutations in the subtree, not attributes
    const observerConfig = {
      attributes: false,
      childList: true,
      subtree: true
    };

    const mutationCallback = (mutationsList: MutationRecord[], _observer: MutationObserver) => {
      for (const mutation of mutationsList) {
        // If there are some added nodes in the mutation
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((currentNode: Node) => {
            if (currentNode instanceof Element) {
              this.checkTriggerPopInElement(currentNode);
            }
          });
        }
      }
    };

    // Create the mutation observer and start observing
    this.mutationObserver = new MutationObserver(mutationCallback);
    this.mutationObserver.observe(targetNode, observerConfig);
  }

  /**
   * Extracts the required keys and opens the pop in
   *
   * @param element containing the two info (titleKey, contentKey) to open the dialog
   */
  private openPopIn(element: Element) {
    const queryParams = this.extractQueryParameters(element);

    // Need the first value of the observable to emit in order for the dialog to open
    firstValueFrom(

      this.lazyLoaderDialogService.open(
        () => import('@refx/shared-components').then((module) => module.PopInComponent),
        {
          data: {
            inputs: {
              titleKey: queryParams.titleKey,
              contentKey: queryParams.contentKey
            },
            outputs: {}
          } as TemplateContext<Record<string, unknown>, PopInContextInput>
        }
      )
    );
  }

  /**
   * Extracts the parameters to be used for the pop-in from the given DOM Element href attribute.
   * Example:
   * Href attribute : href="?titleKey=key1&contentKey=key2"
   * Result: { titleKey: 'key1', contentKey: 'key2' }
   *
   * @param element the DOM Element to extract the href attribute
   */
  private extractQueryParameters(element: Element): Params {
    const href = element.getAttribute('href');
    const urlTree = this.router.parseUrl(href ?? '');
    return urlTree.queryParams;
  }

  /**
   * Monitor the loading states of the sources required during the bootstrap of the application
   *
   * @param shouldMonitorRouterStatus boolean to indicate if the router events should be monitored
   */
  public monitorLoadingStates(shouldMonitorRouterStatus = true) {
    this.uiStatusLoadingService.monitorUiStatusStoreStatus();
    if (shouldMonitorRouterStatus) {
      this.routerLoadingService.monitorRouterEventsStatus(
        (event: any) => !(
          event instanceof Scroll
          || event instanceof NavigationCancel
          || event instanceof RouteConfigLoadEnd
          || event instanceof GuardsCheckStart
          || event instanceof NavigationSkipped
        )
      );
    }
  }

  /**
   * Checks if we are on the base route
   *
   * @returns boolean
   */
  private isOnBaseRoute(): boolean {
    return this.locationStrategy.getBaseHref() === this.document.location.pathname;
  }

  /**
   * Checks if the skeletal loader should be displayed
   */
  public async isSkeletalLoaderEnabled() {
    try {
      const isOnBaseRoute = this.isOnBaseRoute();
      const isEnabled$: Observable<boolean> = this.skeletalLoaderUtilityService.shouldDisplayForRoute$(
        skeletalRoutes,
        [],
        () => isOnBaseRoute
      );
      this.displaySkeletalLoader = await firstValueFrom(isEnabled$);
    } catch {} finally {
      this.monitorLoadingStates(!this.displaySkeletalLoader);
    }
  }

  public ngOnInit() {
    this.initMutationObserver();
    this.isPending$ = combineLatest([
      this.store.pipe(
        select(selectJourneysEntities),
        map((entities) => this.isEntitiesPending(entities)),
        distinctUntilChanged()
      ),
      this.store.pipe(
        select(selectCancelCheckinState),
        map((state: CancelCheckinState) => !!state.isPending)
      ),
      this.store.pipe(
        select(selectSeatmapsEntities),
        map((entities) => this.isEntitiesPending(entities)),
        distinctUntilChanged()
      ),
      this.store.pipe(
        select(selectRegulatoryDetailsEntities),
        map((entities) => this.isEntitiesPending(entities)),
        distinctUntilChanged()
      ),
      this.store.pipe(select(selectSsciPaymentStatusIsPending)),
      this.store.pipe(
        select(selectCheckInPaymentRecordsEntities),
        map((entities) => this.isEntitiesPending(entities)),
        distinctUntilChanged()
      ),
      this.store.pipe(
        select(selectJourneyElementCommons),
        map((journeyElements) => journeyElements.some((journeyElement: AsyncStoreItem) => !!journeyElement.isPending)),
        distinctUntilChanged()
      ),
      this.store.pipe(select(selectCheckInPaymentRecordsStorePendingStatus)),
      this.store.pipe(
        select(selectSeatmapsState),
        map((seatmapElement: AsyncStoreItem) => !!seatmapElement.isPending),
        distinctUntilChanged()
      ),
      this.store.pipe(select(selectJourneyAlternativeOffersIsPendingStatus)),
      this.loadingStateService.shouldDisplayLoader$
    ]).pipe(
      map((arePending: (boolean | undefined)[]) => arePending.some((isPending) => !!isPending))
    );
    this.subscriptions.push(
      this.router.events.pipe(
        withLatestFrom(this.monitorRouterStatus$.asObservable()),
        filter(([event, shouldMonitorRouterStatus]) => shouldMonitorRouterStatus && event instanceof NavigationEnd)
      ).subscribe(() => {
        this.monitorLoadingStates();
        this.monitorRouterStatus$.next(false);
      }),
      this.router.events.pipe(
        filter((event) => event instanceof NavigationStart)
      ).subscribe(() => {
        // Set the T0 for TTI computation when navigation starts
        this.analyticsUtilityService.setPageStartTime();
      })
    );

    this.setupRulesets();
  }

  /**
   * Triggered when a route is activated. This callback is used
   * for downloading all the lazy chunks like the devtools modules
   * and the lazy components rendered throught the lazy outlet
   */
  public onRouteActivated() {
    if (!this.firstRouteActivated) {
      this.firstRouteActivated = true;
      this.monitorRouterStatus$.next(true);
      this.showDebugPanel = this.appInitializationService.isTraceEnabled();
      this.appInitializationService.initializeDevTools();
    }
  }

  public ngOnDestroy() {
    // clean the subscriptions
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    // Destroy the mutation observer
    this.mutationObserver.disconnect();
  }
}
